import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SafeUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { AUTH_SERVICE, AuthService } from '@dibsondance/shared-authentication-base';
import { ENVIRONMENT_SERVICE } from '@dibsondance/shared-environment-base';
import { LoggerService } from '@dibsondance/shared-logging';

/**
 * Host-specific authentication service interface.
 * Extends base AuthService with host-specific user properties.
 */
export interface HostAuthService extends AuthService {
  // Host-specific user properties
  userEmail: Signal<string | null>;
  userName: Signal<string | null>;
  userInitials: Signal<string | null>;
  userPhotoURL: Signal<SafeUrl | null>;
}

/**
 * Host authentication service implementation.
 * Wraps base AUTH_SERVICE and adds host-specific user properties.
 *
 * Note: This service returns boolean for auth operations.
 * Consumers are responsible for navigation based on the result.
 */
@Injectable()
export class HostAuthenticationService implements HostAuthService {
  private readonly authService = inject(AUTH_SERVICE);
  private readonly logger = inject(LoggerService);
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject(ENVIRONMENT_SERVICE);

  // Base AuthService properties
  get user() {
    return this.authService.user;
  }

  get idToken() {
    return this.authService.idToken;
  }

  isLoggedIn(): Signal<boolean> {
    return this.authService.isLoggedIn();
  }

  // Host-specific properties

  get userEmail(): Signal<string | null> {
    return computed(() => this.authService.user()?.email ?? null);
  }

  get userName(): Signal<string | null> {
    return computed(() => this.authService.user()?.displayName ?? null);
  }

  get userInitials(): Signal<string | null> {
    return computed(() => {
      const name = this.userName();
      if (!name) return null;
      return name
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    });
  }

  get userPhotoURL(): Signal<SafeUrl | null> {
    return signal(null); // Not implemented in base AUTH_SERVICE
  }

  async signInWithGoogle(): Promise<boolean> {
    this.logger.info('Starting Google sign-in process', undefined, 'HOST-AUTH');

    const success = await this.authService.signInWithGoogle();

    this.logger.info('Google sign-in result', { success }, 'HOST-AUTH');

    return success;
  }

  async signInWithMagicLink(email: string): Promise<boolean> {
    try {
      this.logger.info('Starting magic link sign-in', undefined, 'HOST-AUTH');

      const redirectUrl = `${window.location.origin}/auth/magic-link`;
      const apiUrl = this.environmentService.environment.apiUrl;
      const url = `${apiUrl}api/auth/send-magic-link`;

      await firstValueFrom(this.http.post(url, { email, redirectUrl }));
      localStorage.setItem('emailForSignIn', email);

      this.logger.info('Magic link sent successfully', undefined, 'HOST-AUTH');
      return true;
    } catch (error) {
      this.logger.error('Magic link send failed', error as Error, undefined, 'HOST-AUTH');
      return false;
    }
  }

  async completeMagicLinkSignIn(emailLink: string): Promise<boolean> {
    this.logger.info('Completing magic link sign-in', undefined, 'HOST-AUTH');
    return await this.authService.completeMagicLinkSignIn(emailLink);
  }

  async signOut(): Promise<boolean> {
    this.logger.info('Starting sign-out process', undefined, 'HOST-AUTH');

    const success = await this.authService.signOut();

    this.logger.info('Sign-out result', { success }, 'HOST-AUTH');

    return success;
  }
}
